import React, { useState } from 'react';

interface AgreementData {
  agreedToTerms: boolean;
  signature: string;
  signedDate: string;
}

interface Step5Props {
  data: AgreementData;
  brokerName: string;
  onNext: () => void;
  onBack: () => void;
  onSave: (data: AgreementData) => void;
}

const Step5Agreement: React.FC<Step5Props> = ({ data, brokerName, onNext, onBack, onSave }) => {
  const [formData, setFormData] = useState<AgreementData>(data);
  const [errors, setErrors] = useState<Partial<Record<keyof AgreementData, string>>>({});
  const [showFullAgreement, setShowFullAgreement] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name as keyof AgreementData]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Partial<Record<keyof AgreementData, string>> = {};

    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the terms to continue';
    }
    if (!formData.signature.trim()) {
      newErrors.signature = 'Signature is required';
    } else if (formData.signature.trim().length < 3) {
      newErrors.signature = 'Please enter your full name';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      const today = new Date().toISOString().split('T')[0];
      onSave({
        ...formData,
        signedDate: today,
      });
      onNext();
    }
  };

  const handleBack = () => {
    onSave(formData);
    onBack();
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Broker-Carrier Agreement</h2>
      <p className="text-gray-600 mb-6">
        Please review and sign the agreement between your carrier and {brokerName || 'the broker'}.
      </p>

      <div className="space-y-6">
        {/* Agreement Preview */}
        <div className="border border-gray-300 rounded-lg">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-300 flex justify-between items-center">
            <h3 className="font-medium text-gray-900">Broker-Carrier Transportation Agreement</h3>
            <button
              type="button"
              onClick={() => setShowFullAgreement(!showFullAgreement)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showFullAgreement ? 'Show Less' : 'Read Full Agreement'}
            </button>
          </div>
          
          <div className={`p-4 ${showFullAgreement ? '' : 'max-h-64'} overflow-y-auto`}>
            <div className="prose prose-sm max-w-none text-gray-700">
              <p className="font-semibold">BROKER-CARRIER AGREEMENT</p>
              <p className="mt-2">
                This Broker-Carrier Agreement ("Agreement") is entered into by and between 
                {brokerName ? ` ${brokerName}` : ' the Broker'} ("Broker") and the undersigned motor carrier ("Carrier").
              </p>

              <p className="mt-4 font-semibold">1. SERVICES</p>
              <p>
                Carrier agrees to provide transportation services for freight tendered by Broker. 
                Carrier shall transport shipments in accordance with the terms specified in each 
                Rate Confirmation issued by Broker.
              </p>

              <p className="mt-4 font-semibold">2. CARRIER REQUIREMENTS</p>
              <p>Carrier represents and warrants that it:</p>
              <ul className="list-disc pl-5 mt-2">
                <li>Is a licensed motor carrier with valid operating authority</li>
                <li>Maintains insurance coverage as required by law and this Agreement</li>
                <li>Will comply with all applicable federal, state, and local laws</li>
                <li>Will not re-broker or co-broker any loads without written consent</li>
              </ul>

              {showFullAgreement && (
                <>
                  <p className="mt-4 font-semibold">3. INSURANCE REQUIREMENTS</p>
                  <p>Carrier shall maintain the following minimum insurance coverage:</p>
                  <ul className="list-disc pl-5 mt-2">
                    <li>Auto Liability: $750,000 minimum</li>
                    <li>Cargo Insurance: $100,000 minimum</li>
                    <li>Workers' Compensation: As required by law</li>
                  </ul>

                  <p className="mt-4 font-semibold">4. PAYMENT TERMS</p>
                  <p>
                    Broker agrees to pay Carrier within thirty (30) days of receipt of the signed 
                    Proof of Delivery (POD) and any other required documentation. Payment will be 
                    made via the method specified by Carrier during onboarding.
                  </p>

                  <p className="mt-4 font-semibold">5. INDEPENDENT CONTRACTOR</p>
                  <p>
                    Carrier is an independent contractor and not an employee, agent, or partner of Broker. 
                    Carrier is responsible for all taxes, fees, and expenses related to its operations.
                  </p>

                  <p className="mt-4 font-semibold">6. CONFIDENTIALITY</p>
                  <p>
                    Carrier agrees to keep confidential all shipper and rate information provided by Broker. 
                    Carrier shall not contact shippers directly for the purpose of soliciting business.
                  </p>

                  <p className="mt-4 font-semibold">7. INDEMNIFICATION</p>
                  <p>
                    Carrier agrees to indemnify and hold harmless Broker from any claims, damages, or 
                    liabilities arising from Carrier's performance of services under this Agreement.
                  </p>

                  <p className="mt-4 font-semibold">8. TERM AND TERMINATION</p>
                  <p>
                    This Agreement shall remain in effect until terminated by either party with thirty (30) 
                    days written notice. Either party may terminate immediately for material breach.
                  </p>

                  <p className="mt-4 font-semibold">9. DOUBLE BROKERING PROHIBITION</p>
                  <p>
                    Carrier shall not re-broker, co-broker, or assign any load to another carrier without 
                    prior written consent from Broker. Violation of this provision shall result in immediate 
                    termination and may result in legal action.
                  </p>

                  <p className="mt-4 font-semibold">10. GOVERNING LAW</p>
                  <p>
                    This Agreement shall be governed by the laws of the state in which Broker's principal 
                    place of business is located.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Agreement Checkbox */}
        <div className={`p-4 rounded-lg ${errors.agreedToTerms ? 'bg-red-50 border border-red-300' : 'bg-gray-50'}`}>
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              name="agreedToTerms"
              checked={formData.agreedToTerms}
              onChange={handleChange}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 mt-0.5"
            />
            <span className="ml-3 text-gray-700">
              I have read and agree to the terms of this Broker-Carrier Agreement. I understand that 
              this is a legally binding contract and that I am authorized to sign on behalf of my company.
            </span>
          </label>
          {errors.agreedToTerms && (
            <p className="text-red-500 text-sm mt-2 ml-8">{errors.agreedToTerms}</p>
          )}
        </div>

        {/* Electronic Signature */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Electronic Signature *
          </label>
          <p className="text-gray-500 text-sm mb-3">
            Type your full legal name below to sign this agreement electronically.
          </p>
          <input
            type="text"
            name="signature"
            value={formData.signature}
            onChange={handleChange}
            placeholder="Type your full name"
            className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg italic ${
              errors.signature ? 'border-red-500' : 'border-gray-300'
            }`}
            style={{ fontFamily: 'cursive, serif' }}
          />
          {errors.signature && (
            <p className="text-red-500 text-sm mt-1">{errors.signature}</p>
          )}
          
          {formData.signature && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <p className="text-sm text-gray-500 mb-2">Signature Preview:</p>
              <p className="text-2xl italic text-gray-800" style={{ fontFamily: 'cursive, serif' }}>
                {formData.signature}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Date: {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">About Electronic Signatures</p>
              <p>
                By typing your name above, you agree that your electronic signature is the legal 
                equivalent of your manual signature on this Agreement, in accordance with the 
                Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <button
          onClick={handleBack}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          ← Back
        </button>
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

export default Step5Agreement;